import { FC, ReactNode, isValidElement } from "react";
import ModalBoxCore from "../../core-components/modal-box/ModalBoxCore";
import ModalHeader from "../../core-components/modal-box/modal-header/ModalHeader";
import ModalBody from "../../core-components/modal-box/modal-body/ModalBody";
import { Box, Typography } from "@mui/material";
import palette from "../../../theme/palette";

interface iCompleteMenuModalProps {
	open: boolean;
	onClose: () => void;
	menu: ReactNode;
}

const completeMenuSx = {
	whiteSpace: "pre-wrap",
	"& strong, & b, & h1, & h2, & h3, & h4, & h5, & h6": {
		fontWeight: 500,
	},
	// Category labels that sit directly above a list (e.g. "Desserts:")
	"& p:has(+ ul), & div:has(+ ul)": {
		fontWeight: 500,
	},
	"& ul": {
		listStyle: "none",
		mb: 2,
		"&:last-child": { mb: 0 },
		"& li": {
			"&:before": {
				content: "'• '",
			},
		},
	},
} as const;

const looksLikeHtml = (value: string): boolean => /<\/?[a-z][\s\S]*>/i.test(value);

const isBulletLine = (line: string): boolean => /^[•\-\*]\s+/.test(line);

const isHeadingLine = (line: string): boolean => {
	if (!line || isBulletLine(line)) {
		return false;
	}
	if (line.endsWith(":")) {
		return true;
	}
	// Short standalone titles like "Buffet"
	return !line.includes(",") && line.length <= 40;
};

const renderPlainTextMenu = (menu: string) => {
	const lines = menu.replace(/\r\n/g, "\n").split("\n");

	return lines.map((rawLine, index) => {
		const line = rawLine.trim();
		if (!line) {
			return <Box key={`empty-${index}`} sx={{ height: 8 }} />;
		}

		if (isHeadingLine(line)) {
			return (
				<Typography
					key={`heading-${index}`}
					component="div"
					variant="inherit"
					sx={{
						fontWeight: 500,
						mt: index === 0 ? 0 : 1
					}}>
					{line}
				</Typography>
			);
		}

		if (isBulletLine(line)) {
			return (
				<Typography key={`item-${index}`} component="div" variant="inherit">
					{line.startsWith("•") ? line : `• ${line.replace(/^[-\*]\s+/, "")}`}
				</Typography>
			);
		}

		// Comma-separated values on a non-heading line → dotted list
		if (line.includes(",")) {
			return line
				.split(",")
				.map((part) => part.trim())
				.filter(Boolean)
				.map((part, partIndex) => (
					<Typography
						key={`csv-${index}-${partIndex}`}
						component="div"
						variant="inherit"
					>
						• {part}
					</Typography>
				));
		}

		return (
			<Typography key={`line-${index}`} component="div" variant="inherit">
				{line}
			</Typography>
		);
	});
};

const CompleteMenuModal: FC<iCompleteMenuModalProps> = ({ open, onClose, menu }) => {
	const isStringMenu = typeof menu === "string";

	return (
		<>
			<ModalBoxCore open={open} onClose={onClose} className="completeMenuModal">
				<ModalHeader modalHeaderText="Menu" closeModal={onClose} />
				<ModalBody>
					{isStringMenu ? (
						looksLikeHtml(menu) ? (
							<Box
								dangerouslySetInnerHTML={{ __html: menu }}
								sx={[{
									color: palette.secondary.main
								}, ...(Array.isArray(completeMenuSx) ? completeMenuSx : [completeMenuSx])]} />
						) : (
							<Box
								sx={[{
									color: palette.secondary.main
								}, ...(Array.isArray(completeMenuSx) ? completeMenuSx : [completeMenuSx])]}>
								{renderPlainTextMenu(menu)}
							</Box>
						)
					) : (
						<Box
							sx={[{
								color: palette.secondary.main
							}, ...(Array.isArray(completeMenuSx) ? completeMenuSx : [completeMenuSx])]}>
							{isValidElement(menu) || menu == null ? menu : String(menu)}
						</Box>
					)}
				</ModalBody>
			</ModalBoxCore>
		</>
	);
};
export default CompleteMenuModal;
